import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface DebugContextType {
  isDebugEnabled: boolean;
  toggleDebug: () => void;
}

const DebugContext = createContext<DebugContextType | undefined>(undefined);

const DEBUG_KEY = 'partxplorer_debug_mode';

const originalConsole = {
  log: console.log,
  warn: console.warn,
  error: console.error,
};

export function DebugProvider({ children }: { children: ReactNode }) {
  const [isDebugEnabled, setIsDebugEnabled] = useState(() => {
    const saved = localStorage.getItem(DEBUG_KEY);
    return saved === 'true';
  });

  useEffect(() => {
    localStorage.setItem(DEBUG_KEY, isDebugEnabled.toString());
  }, [isDebugEnabled]);

  useEffect(() => {
    if (isDebugEnabled) {
      console.log = originalConsole.log;
      console.warn = originalConsole.warn;
      console.error = originalConsole.error;
    } else {
      console.log = () => {};
      console.warn = () => {};
      console.error = () => {};
    }
  }, [isDebugEnabled]);

  const toggleDebug = () => {
    setIsDebugEnabled(prev => !prev);
  };

  return (
    <DebugContext.Provider value={{ isDebugEnabled, toggleDebug }}>
      {children}
    </DebugContext.Provider>
  );
}

export function useDebug() {
  const context = useContext(DebugContext);
  if (context === undefined) {
    throw new Error('useDebug must be used within a DebugProvider');
  }
  return context;
}

// Helper function for conditional logging
export function debugLog(...args: any[]) {
  const isDebug = localStorage.getItem(DEBUG_KEY) === 'true';
  if (isDebug) {
    console.log(...args);
  }
}

export function debugWarn(...args: any[]) {
  const isDebug = localStorage.getItem(DEBUG_KEY) === 'true';
  if (isDebug) {
    console.warn(...args);
  }
}

export function debugError(...args: any[]) {
  const isDebug = localStorage.getItem(DEBUG_KEY) === 'true';
  if (isDebug) {
    console.error(...args);
  }
}
